'use client';

import * as React from 'react';
import { useMediaQuery } from '../../lib/hooks/use-media-query';
import { cn } from '../../lib/utils';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from './drawer';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from './sheet';

interface BaseProps {
	children: React.ReactNode;
}

interface RootPanelProps extends BaseProps {
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

interface PanelProps extends BaseProps {
	className?: string;
	asChild?: true;
	side?: 'top' | 'right' | 'bottom' | 'left';
}

const mobile = '(max-width: 767px)';

const Panel = ({ children, ...props }: RootPanelProps) => {
	const isMobile = useMediaQuery(mobile);
	const Panel = isMobile ? Drawer : Sheet;

	return <Panel {...props}>{children}</Panel>;
};

const PanelTrigger = ({ className, children, ...props }: PanelProps) => {
	const isMobile = useMediaQuery(mobile);
	const PanelTrigger = isMobile ? DrawerTrigger : SheetTrigger;

	return (
		<PanelTrigger className={className} {...props}>
			{children}
		</PanelTrigger>
	);
};

const PanelClose = ({ className, children, ...props }: PanelProps) => {
	const isMobile = useMediaQuery(mobile);
	const PanelClose = isMobile ? DrawerClose : SheetClose;

	return (
		<PanelClose className={className} {...props}>
			{children}
		</PanelClose>
	);
};

const PanelContent = ({ className, children, side = 'right', ...props }: PanelProps) => {
	const isMobile = useMediaQuery(mobile);
	const PanelContent = isMobile ? DrawerContent : SheetContent;

	return (
		<PanelContent className={cn(isMobile && 'h-[80vh]', 'p-0 md:max-w-xl', className)} side={isMobile ? 'bottom' : side} {...props}>
			<div className="h-full flex flex-col">{children}</div>
		</PanelContent>
	);
};

const PanelDescription = ({ className, children, ...props }: PanelProps) => {
	const isMobile = useMediaQuery(mobile);
	const PanelDescription = isMobile ? DrawerDescription : SheetDescription;

	return (
		<PanelDescription className={className} {...props}>
			{children}
		</PanelDescription>
	);
};

const PanelHeader = ({ className, children, ...props }: PanelProps) => {
	const isMobile = useMediaQuery(mobile);
	const PanelHeader = isMobile ? DrawerHeader : SheetHeader;

	return (
		<PanelHeader className={cn('flex-shrink-0', 'px-4', className)} {...props}>
			{children}
		</PanelHeader>
	);
};

const PanelTitle = ({ className, children, ...props }: PanelProps) => {
	const isMobile = useMediaQuery(mobile);
	const PanelTitle = isMobile ? DrawerTitle : SheetTitle;

	return (
		<PanelTitle className={className} {...props}>
			{children}
		</PanelTitle>
	);
};

const PanelBody = ({ className, children, ...props }: PanelProps) => {
	const isMobile = useMediaQuery(mobile);

	return (
		<div className={cn('flex-1 overflow-y-auto px-4', isMobile && 'max-h-[calc(80vh-8rem)]', className)} {...props}>
			{children}
		</div>
	);
};

const PanelFooter = ({ className, children, ...props }: PanelProps) => {
	const isMobile = useMediaQuery(mobile);
	const PanelFooter = isMobile ? DrawerFooter : SheetFooter;

	return (
		<PanelFooter className={cn('flex-shrink-0', 'px-4', className)} {...props}>
			{children}
		</PanelFooter>
	);
};

export { Panel, PanelBody, PanelClose, PanelContent, PanelDescription, PanelFooter, PanelHeader, PanelTitle, PanelTrigger };
